"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createClient = exports.isServer = void 0;
const axios_1 = __importDefault(require("axios"));
const config_1 = __importDefault(require("next/config"));
const query_string_1 = __importDefault(require("query-string"));
const uuid_1 = require("uuid");
const notificationEventBus_1 = require("../utils/notificationEventBus");
const sentryHandler_1 = require("../utils/sentry/sentryHandler");
exports.isServer = typeof window === 'undefined';
const makeEndpoint = (url, baseUrl) => {
    const { webPath } = config_1.default().publicRuntimeConfig;
    if (exports.isServer && (url === null || url === void 0 ? void 0 : url.startsWith('/'))) {
        url = `${baseUrl}${url}`;
        if (!baseUrl || baseUrl.startsWith('/')) {
            url = `${webPath}${url}`;
        }
    }
    return url;
};
const createClient = (baseURL, { sentryErrorTagName, logger }) => {
    const retriedRequests = {};
    const client = axios_1.default.create({
        baseURL,
        timeout: 20000,
        responseType: 'json',
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        paramsSerializer: params => query_string_1.default.stringify(params),
        transformRequest: (data, _headers) => {
            if (typeof data === 'string')
                return data;
            return JSON.stringify(data);
        },
        /** That's need because axios return string on a server and object on a client */
        transformResponse: (data, headers) => {
            var _a;
            if (typeof data === 'string' && ((_a = headers['content-type']) === null || _a === void 0 ? void 0 : _a.includes('application/json'))) {
                try {
                    data = JSON.parse(data);
                }
                catch (e) { }
            }
            return data;
        },
    });
    client.interceptors.request.use(config => {
        if (!config.headers['X-Request-Id']) {
            config.headers['X-Request-Id'] = uuid_1.v4();
        }
        config.url = makeEndpoint(config.url, config.baseURL);
        return config;
    });
    client.interceptors.response.use(res => {
        const requestQueryString = query_string_1.default.stringify(res.config.params);
        if (exports.isServer && logger) {
            logger.log({
                level: 'info',
                message: 'REQUEST OK',
                http_status: res.status,
                timestamp: new Date(),
                request_url: `${res.config.url}/${requestQueryString && '?'}${requestQueryString}`,
                body: res.config.data,
            });
        }
        return res;
    }, (error) => {
        var _a, _b, _c, _d, _e;
        const requestId = (((_a = error === null || error === void 0 ? void 0 : error.config) === null || _a === void 0 ? void 0 : _a.headers) && ((_b = error.config) === null || _b === void 0 ? void 0 : _b.headers['X-Request-Id']));
        if (!exports.isServer && !retriedRequests[requestId]) {
            error.id = requestId;
            if (!error.config.silentError) {
                sentryHandler_1.sentryHandler(error, { tag: `${sentryErrorTagName}-client-request-error`, extra: (_c = error === null || error === void 0 ? void 0 : error.response) === null || _c === void 0 ? void 0 : _c.data });
                !exports.isServer && notificationEventBus_1.dispatchEvent(error);
            }
        }
        if (exports.isServer && logger) {
            const requestQueryString = query_string_1.default.stringify(error.config.params);
            logger.log({
                level: 'error',
                message: 'REQUEST ERROR',
                http_status: (_d = error.response) === null || _d === void 0 ? void 0 : _d.status,
                request_url: `${error.config.url}/${requestQueryString && '?'}${requestQueryString}`,
                meta: JSON.stringify(Object.assign({ query: error.config.params, headers: error.config.headers, body: error.config.data }, (error.meta && error.meta))),
                exception: JSON.stringify({
                    message: error.message,
                    body: (_e = error.response) === null || _e === void 0 ? void 0 : _e.data,
                    stack: error.stack,
                }),
                timestamp: new Date(),
                request_id: requestId,
            });
        }
        throw error;
    });
    return client;
};
exports.createClient = createClient;
